import Card from 'components/Card';
import ProposalShow from 'components/Proposal/ProposalShow';
import {
  RequiredFieldInput,
  RosterRequestDataNewGroupReservation,
  RosterRequestQuery,
} from 'generated/graphql';
import { useAppSelector } from 'store/store';
import { calculateHotelNights } from 'utilities/calculateHotelNights';
import { getControlFieldLabelForPod } from 'utilities/podBasedFiltering';

import GroupReservationRequiredFields from '../RequestDetails/GroupReservationRequiredFields';

import MeetingSpaceRequirementsCard from './MeetingSpaceRequirementsCard';

export type UnconfimedProps = {
  requestData: RosterRequestDataNewGroupReservation;
  request?: RosterRequestQuery['rosterRequest'];
  onUpdateRequiredFields: (requiredFields: RequiredFieldInput[]) => void;
  loading: boolean;
};
const Unconfirmed = ({
  requestData,
  request,
  onUpdateRequiredFields,
  loading,
}: UnconfimedProps) => {
  const { pod: clientPod } = useAppSelector((state) => state.clientConfig);
  const {
    breakfast,
    freeParking,
    kitchen,
    washerDryer,
    meetingSpace,
    meetingSpaceRequirements,
    otherAmenities,
    distanceFromDestination,
    brandPreference,
    checkInDate,
    checkOutDate,
    contactEmail,
    contactPhone,
    controlNum,
  } = requestData;
  const proposalId = request?.proposalId;
  const numOfNight = calculateHotelNights(checkInDate, checkOutDate);

  // Helper function to format enum values to prettier text
  const formatAmenityValue = (value: string): string => {
    switch (value) {
      case 'NICE_TO_HAVE':
        return 'Nice to have';
      case 'NOT_REQUIRED':
        return 'Not required';
      case 'REQUIRED':
        return 'Required';
      default:
        // Fallback: convert underscores to spaces and capitalize first letter
        return value
          .toLowerCase()
          .replace(/_/g, ' ')
          .replace(/\b\w/g, (char) => char.toUpperCase());
    }
  };
  return (
    <>
      {/* Proposal Card */}
      {proposalId && (
        <Card className="overflow-visible">
          <h2 className="font-oswald text-2xl font-bold uppercase text-secondaryBlue">
            Your Proposal
          </h2>
          <ProposalShow id={proposalId} totalNights={numOfNight} />
        </Card>
      )}

      <Card className="overflow-visible">
        <h2 className="font-oswald text-2xl font-bold uppercase text-secondaryBlue">
          Amenities
        </h2>
        <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <p className="mb-2 text-sm font-medium text-gray-500">Breakfast</p>
            <p className="font-arimo text-base font-medium text-gray-900">
              {formatAmenityValue(breakfast)}
            </p>
          </div>
          <div>
            <p className="mb-2 text-sm font-medium text-gray-500">
              Free Parking
            </p>
            <p className="font-arimo text-base font-medium text-gray-900">
              {formatAmenityValue(freeParking)}
            </p>
          </div>
          <div>
            <p className="mb-2 text-sm font-medium text-gray-500">
              Kitchen/Kitchenette
            </p>
            <p className="font-arimo text-base font-medium text-gray-900">
              {formatAmenityValue(kitchen)}
            </p>
          </div>
          <div>
            <p className="mb-2 text-sm font-medium text-gray-500">
              Washer/Dryer
            </p>
            <p className="font-arimo text-base font-medium text-gray-900">
              {formatAmenityValue(washerDryer)}
            </p>
          </div>
          <div>
            <p className="mb-2 text-sm font-medium text-gray-500">
              Meeting Space
            </p>
            <p className="font-arimo text-base font-medium text-gray-900">
              {formatAmenityValue(meetingSpace)}
            </p>
          </div>
          {otherAmenities && (
            <div className="col-span-full">
              <p className="mb-2 text-sm font-medium text-gray-500">
                Other Amenities
              </p>
              <p className="font-arimo text-base font-medium text-gray-900">
                {otherAmenities}
              </p>
            </div>
          )}
        </div>
      </Card>

      <MeetingSpaceRequirementsCard
        meetingSpace={meetingSpace}
        meetingSpaceRequirements={meetingSpaceRequirements}
      />

      <Card className="overflow-visible">
        <h2 className="font-oswald text-2xl font-bold uppercase text-secondaryBlue">
          Trip Preferences
        </h2>
        <div className="mt-6 space-y-4">
          <div>
            <p className="mb-2 text-sm font-medium text-gray-500">
              Distance from Destination
            </p>
            <p className="font-arimo text-base font-medium text-gray-900">
              Within {distanceFromDestination} miles
            </p>
          </div>
          {brandPreference && (
            <div>
              <p className="mb-2 text-sm font-medium text-gray-500">
                Brand Preference
              </p>
              <p className="font-arimo text-base font-medium text-gray-900">
                {brandPreference}
              </p>
            </div>
          )}
        </div>
      </Card>
      <Card className="overflow-visible">
        <h2 className="font-oswald text-2xl font-bold uppercase text-secondaryBlue">
          Contact Details
        </h2>
        <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <p className="mb-2 text-sm font-medium text-gray-500">Email</p>
            <p className="font-arimo text-base font-medium text-gray-900">
              {contactEmail}
            </p>
          </div>
          <div>
            <p className="mb-2 text-sm font-medium text-gray-500">Phone</p>
            <p className="font-arimo text-base font-medium text-gray-900">
              {contactPhone}
            </p>
          </div>
          <div className="md:col-span-2">
            <p className="mb-2 text-sm font-medium text-gray-500">
              {getControlFieldLabelForPod(clientPod)}
            </p>
            <p className="font-arimo text-base font-medium text-gray-900">
              {controlNum}
            </p>
          </div>
        </div>
      </Card>
      <Card className="overflow-visible">
        <h2 className="font-oswald text-2xl font-bold uppercase text-secondaryBlue">
          Reservation Details
        </h2>
        <GroupReservationRequiredFields
          requestData={requestData}
          onUpdateRequiredFields={onUpdateRequiredFields}
          loading={loading}
          hasBookings={(request?.rosterRoomAssignments?.length || 0) > 0}
        />
      </Card>
    </>
  );
};

export default Unconfirmed;
